/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  HalfContetntLayout,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import CustomGrid from 'components/grids/CustomGrid';
import { ComboBox } from 'components/selects/ComboBox';
import { findMdLineListForInvest } from 'apis/md/MdLine';
import { findMdBldoList } from 'apis/md/MdBldo';
import { MdBldo } from 'models/md/MdBldo';
import { MdLine } from 'models/md/MdLine';

type Props = {
  open: boolean;
  close: () => void;
  selectedItems: MdLine[];
  condition: ConditionProps;
  callbackFn: (items: MdLine[]) => void;
};

interface ConditionProps {
  prdnLnTpCd: string;
  prdnLnTpCdNm?: string;
  bildPlntCds: string[];
  bildPlntCdNms?: string[];
  bldoId?: string;
}

/**
 * 전극/셀 라인 선택 팝업
 */
const EltrCellLineInqPopup = ({ open, close, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const gridRef = useRef<any>();
  const selectedGridRef = useRef<any>();
  const [bldoCdList, setBldoCdList] = useState<MdBldo[]>([]);
  const [condition, setCondition] = useState<ConditionProps>(props.condition);
  const [selectedItems, setSelectedItems] = useState<MdLine[]>(props.selectedItems);
  const [rowData, setRowData] = useState<MdLine[]>([]);

  const layoutDefinition = [
    {
      header: String(t('com.label.라인유형', '라인유형')),
      binding: 'prdnLnTpCdNm',
      align: 'left',
      width: '*',
    },
    {
      binding: 'bildPlntCdNm',
      header: String(t('com.label.플랜트', '플랜트')),
      width: '*',
    },
    {
      binding: 'bldoNm',
      header: String(t('com.label.동', '동')),
      align: 'left',
      width: '*',
    },
    {
      binding: 'prdnLnNm',
      header: String(t('com.label.라인', '라인')),
      align: 'left',
      width: '*',
    },
  ];

  const bldoCode = useMemo(() => {
    return bldoCdList.map((o) => ({
      cmnCd: o.bldoId,
      cmnCdNm: o.bldoNm,
    }));
  }, [bldoCdList]);

  const initCodes = async () => {
    const bldoList = await findMdBldoList({});
    setBldoCdList(bldoList || []);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => {
    setSelectedItems((prev) => _.uniqBy([...prev, ...gridRef.current.selectedItems], 'prdnLnId'));
  };

  const handleDelRow = () => {
    const result = selectedItems.filter((o) => !selectedGridRef.current.selectedItems.includes(o));
    setSelectedItems(result);
  };

  const handleConfirm = () => {
    if (!selectedItems.length) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.추가된 데이터가 없습니다.', '추가된 데이터가 없습니다.'),
      });
      return;
    }

    props.callbackFn(selectedItems);
    close();
  };

  const handleSearch = () => {
    openLoading(true);
    findMdLineListForInvest(condition)
      .then((res) => {
        setRowData(res);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
        });
      })
      .finally(() => openLoading(false));
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const onInitialized2 = (grid) => {
    selectedGridRef.current = grid;
  };

  useEffect(() => {
    initCodes();
    handleSearch();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('md.label.전극/셀 라인 추가', '전극/셀 라인 추가'))}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="popup">
              <SearchCols>
                <label>{t('com.label.라인유형', '라인유형')}</label>
                <CustomInputWithSearch
                  name="prdnLnTpCdNm"
                  value={condition?.prdnLnTpCdNm}
                  readOnly
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.플랜트', '플랜트')}</label>
                <CustomInputWithSearch
                  name="bildPlntCd"
                  value={condition?.bildPlntCds?.join()}
                  readOnly
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.동', '동')}</label>
                <ComboBox
                  classNameWrap="grow"
                  options={bldoCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition?.bldoId}
                  onChange={(value) => handleOnChange('bldoId', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={{
              userId: '',
              empNm: '',
              deptNm: '',
              useYn: 'Y',
            }}
            setReset={setCondition}
            onSearch={handleSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <HalfContetntLayout style={{ marginTop: '24px' }}>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.생산라인 목록', '생산라인 목록')}</h3>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="lineGrid"
            layoutDefinition={layoutDefinition}
            rowData={rowData}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={onInitialized}
          />
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup></SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.label.삭제', '삭제')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="selectedLineGrid"
            layoutDefinition={layoutDefinition}
            rowData={selectedItems}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={onInitialized2}
          />
        </div>
      </HalfContetntLayout>
    </CustomDialog>
  );
};

export default EltrCellLineInqPopup;
